'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronLeft, CircleCheck, Images, PenLine, Sparkles, X } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { AIGlowOverlay, LoadingSpinner, NoticeBanner } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { useOverlayScrollLock } from '@/shared/hooks/use-overlay-scroll-lock';
import type { FirstRunState } from '@/types/profile';
import {
  FIRST_RUN_CUSTOM_MAX_LENGTH,
  normalizeCustomText,
} from '../lib/first-run-logic';
import {
  useFirstRunWizard,
  type FirstRunPhoto,
} from '../hooks/use-first-run-wizard';

type Wizard = ReturnType<typeof useFirstRunWizard>;

/**
 * First-run wizard: one photo, one choice, then the design page with the
 * render already running. Visual parity with the Apple `FirstRunView`
 * (screen padding 16, card radius 16 / padding 16 / gap 12, photo radius 12,
 * content capped at 560 on wide screens).
 */
export function FirstRunWizard({
  state,
}: {
  state: FirstRunState & { project_id: string };
}) {
  const wizard = useFirstRunWizard(state);
  const t = useTranslations('onboarding.firstRun');
  const tCommon = useTranslations('common');
  const inputRef = useRef<HTMLInputElement>(null);

  // The page underneath must not keep its own scrollbar beside the overlay.
  useOverlayScrollLock(true);
  const openPicker = () => inputRef.current?.click();

  return (
    // Full-screen takeover above the app chrome (Apple fullScreenCover).
    <div className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-background dark:bg-black">
      <div className="flex shrink-0 px-2">
        <button
          type="button"
          onClick={wizard.step === 'choose' ? wizard.backToUpload : wizard.abandon}
          aria-label={wizard.step === 'choose' ? tCommon('back') : tCommon('close')}
          className="flex size-11 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
        >
          {wizard.step === 'choose' ? (
            <ChevronLeft className="size-5 rtl:-scale-x-100" strokeWidth={2.5} />
          ) : (
            <X className="size-5" strokeWidth={2.5} />
          )}
        </button>
      </div>

      {/* One file only. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) wizard.pickFile(file);
        }}
      />

      <div
        className={cn(
          'mx-auto flex w-full max-w-[560px] flex-1 flex-col gap-3 px-4 pt-3',
          // Phones: screen 1 carries its own bottom inset (buttons pinned low).
          wizard.step === 'upload' ? 'pb-5 max-md:pb-0' : 'pb-5'
        )}
      >
        {wizard.step === 'upload' ? (
          <UploadStep t={t} onPick={openPicker} onSkip={wizard.skip} />
        ) : (
          <ChooseStep t={t} wizard={wizard} onPickAnother={openPicker} />
        )}
      </div>
    </div>
  );
}

type T = ReturnType<typeof useTranslations<'onboarding.firstRun'>>;

function UploadStep({
  t,
  onPick,
  onSkip,
}: {
  t: T;
  onPick: () => void;
  onSkip: () => void;
}) {
  return (
    <>
      <h1 className="text-screen-title !font-normal">{t('uploadTitle')}</h1>
      <p className="text-card-body text-muted-foreground">{t('uploadHint')}</p>
      {/* Phones: the buttons sit at the bottom of the viewport for thumb reach;
          the column scrolls when the content is taller. */}
      <div className="flex flex-col items-center gap-3 pt-5 max-md:mt-auto max-md:pb-[calc(20px+env(safe-area-inset-bottom))]">
        <Button variant="teal" size="cta" className="w-full !font-semibold" onClick={onPick}>
          <Images />
          {t('choosePhoto')}
        </Button>
<SkipLink t={t} onClick={onSkip} />
      </div>
    </>
  );
}

function ChooseStep({
  t,
  wizard,
  onPickAnother,
}: {
  t: T;
  wizard: Wizard;
  onPickAnother: () => void;
}) {
  const [customOpen, setCustomOpen] = useState(wizard.choice?.kind === 'custom');
  const { photo, choice, isLaunching } = wizard;
  // The choice waits only while the photo is on its way; after a refusal or a
  // failure the photo status says what to do next.
  const queued = photo.status === 'uploading' || photo.status === 'checking';
  const hasText = normalizeCustomText(wizard.customText) !== null;

  return (
    <>
      <PhotoPanel t={t} photo={photo} wizard={wizard} onPickAnother={onPickAnother} />

      <h1 className="text-screen-title !font-normal pt-3">{t('chooseTitle')}</h1>

      {wizard.launchError && (
        <NoticeBanner variant="error" message={wizard.launchError} />
      )}

      <ChoiceCard selected={choice?.kind === 'beautify'}>
        <ChoiceHeader
          icon={Sparkles}
          filledIcon
          title={t('beautifyTitle')}
          subtitle={t('beautifySubtitle')}
          selected={choice?.kind === 'beautify'}
          disabled={isLaunching}
          onClick={() => {
            setCustomOpen(false);
            wizard.makeChoice({ kind: 'beautify' });
          }}
        />
        {choice?.kind === 'beautify' && queued && <QueuedNote t={t} />}
      </ChoiceCard>

      <ChoiceCard selected={choice?.kind === 'custom'}>
        <ChoiceHeader
          icon={PenLine}
          title={t('customTitle')}
          subtitle={t('customSubtitle')}
          selected={choice?.kind === 'custom'}
          disabled={isLaunching}
          onClick={() => setCustomOpen(true)}
        />
        {customOpen && (
          <>
            <Textarea
              autoFocus
              rows={3}
              value={wizard.customText}
              maxLength={FIRST_RUN_CUSTOM_MAX_LENGTH}
              placeholder={t('customPlaceholder')}
              disabled={isLaunching}
              onChange={(event) => wizard.setCustomText(event.target.value)}
              className="text-card-body max-h-[9.5em] min-h-0 w-full resize-none rounded-[12px] border-0 bg-background p-3 shadow-none focus-visible:ring-0"
            />
            <Button
              variant="teal"
              size="cta"
              className="self-end !font-semibold"
              disabled={!hasText || isLaunching}
              onClick={() =>
                wizard.makeChoice({ kind: 'custom', text: wizard.customText })
              }
            >
              {t('continue')}
            </Button>
          </>
        )}
        {choice?.kind === 'custom' && queued && <QueuedNote t={t} />}
      </ChoiceCard>

      <div className="flex justify-center pt-3">
<SkipLink t={t} onClick={wizard.skip} disabled={isLaunching} />
      </div>
    </>
  );
}

function QueuedNote({ t }: { t: T }) {
  return (
    <div className="flex items-center gap-2 text-card-body text-muted-foreground">
      <LoadingSpinner size="sm" className="!font-semibold" />
      {t('queuedNote')}
    </div>
  );
}

function ChoiceCard({
  selected,
  children,
}: {
  selected: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-[16px] bg-card-elevated p-4',
        // Apple strokeBorder(accent, 2): drawn inside the card edge.
        selected && 'shadow-[inset_0_0_0_2px_var(--color-primary)]'
      )}
    >
      {children}
    </div>
  );
}

function ChoiceHeader({
  icon: Icon,
  filledIcon = false,
  title,
  subtitle,
  selected,
  disabled,
  onClick,
}: {
  icon: typeof Sparkles;
  /** SF Symbols render `sparkles` solid; lucide draws outlines. */
  filledIcon?: boolean;
  title: string;
  subtitle: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className="flex w-full items-start gap-3 text-start disabled:opacity-60"
    >
      <span className="flex w-7 shrink-0 justify-center">
        <Icon className={cn('size-6 text-primary', filledIcon && 'fill-current')} />
      </span>
      <span className="flex flex-col gap-1">
        <span className="text-card-title">{title}</span>
        <span className="text-card-body text-muted-foreground">{subtitle}</span>
      </span>
    </button>
  );
}

function PhotoPanel({
  t,
  photo,
  wizard,
  onPickAnother,
}: {
  t: T;
  photo: FirstRunPhoto;
  wizard: Wizard;
  onPickAnother: () => void;
}) {
  const failed =
    photo.status === 'rejected' ||
    photo.status === 'technical_error' ||
    photo.status === 'upload_failed';

  return (
    <div className="flex flex-col gap-3">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[12px] bg-card-elevated">
        {photo.previewUrl && (
          <img src={photo.previewUrl} alt="" className="size-full object-cover" />
        )}
        <AIGlowOverlay isActive={photo.status === 'checking'} />
      </div>

      {failed ? (
        <NoticeBanner
          variant="error"
          message={photo.message}
          action={
            <div className="flex gap-3">
              <Button variant="teal" size="sm" className="!font-semibold" onClick={onPickAnother}>
                {t('otherPhoto')}
              </Button>
              {photo.canRetry && (
                <Button variant="secondary" size="sm" className="!font-semibold" onClick={wizard.retry}>
                  {t('retry')}
                </Button>
              )}
            </div>
          }
        />
      ) : photo.status === 'timeout' ? (
        <>
          <StatusText text={t('statusTimeout')} />
          <div className="flex gap-3">
            <Button variant="teal" size="cta" className="!font-semibold" onClick={wizard.waitMore}>
              {t('waitMore')}
            </Button>
            <Button variant="secondary" size="cta" className="!font-semibold" onClick={wizard.abandon}>
              {t('later')}
            </Button>
          </div>
        </>
      ) : photo.status === 'valid' ? (
        <p className="flex items-center gap-2">
          <CircleCheck className="size-5 fill-primary text-background" />
          <StatusText text={t('statusOk')} />
        </p>
      ) : photo.status === 'uploading' || photo.status === 'checking' ? (
        <div className="flex items-center gap-2">
          <LoadingSpinner size="sm" className="!font-semibold" />
          <StatusText
            text={
              photo.status === 'uploading'
                ? t('statusUploading')
                : photo.isSlow
                  ? t('statusSlow')
                  : t('statusChecking')
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function StatusText({ text }: { text: string }) {
  return <span className="text-card-body text-muted-foreground">{text}</span>;
}

/** Quiet text link: the photo buttons above are the choice, this is the way out. */
function SkipLink({ t, onClick, disabled }: { t: T; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="min-h-11 px-3 text-card-body text-muted-foreground underline underline-offset-4 disabled:opacity-50"
      onClick={onClick}
      disabled={disabled}
    >
      {t('skip')}
    </button>
  );
}
